import { useMemo } from 'react';

import {
    fetchGetApplication,
    fetchGetContext,
    fetchGetContextByStateKey,
    fetchGetSplitTest,
    fetchGetStateByStateKey,
    GetApplicationResponse,
    GetContextResponse,
    GetEmailAvailabilityResponse,
    GetStateByStateKeyResponse
} from '@iwoca/lapi-client/edge';
import {
    API_AUTH_TOKEN,
    fetchLendingAPIJSON
} from '@iwoca/lapi-client/lendingAPI';
import { useQuery } from '@tanstack/react-query';

import {
    normaliseApplication,
    normaliseState
} from '@src/containers/GetStarted/GetStarted.helpers';
import { useImpersonatedStateKey } from '@src/hooks/useStateKey';
import { getIsLoggedInCookie } from '@src/utils/Cookies';

export type TGetStateResponse = GetStateByStateKeyResponse & {
    data: {
        ui?: Record<string, unknown>;
    };
};

// TODO: Fix lapi-client to allow fetchGetEmailAvailability to use the correct API_AUTH_TOKEN so iwoca apps calling it don't get permission denied.
export const fetchGetEmailAvailability = async (email: string) => {
    const response = await fetchLendingAPIJSON<GetEmailAvailabilityResponse>(
        '/api/lending/edge/email_availability/',
        {
            query: { email },
            overrideHeaders: {
                authorization: API_AUTH_TOKEN
            }
        }
    );

    return response.data.available;
};

export function useGetContext() {
    const impersonatedStateKey = useImpersonatedStateKey();

    const { data } = useQuery({
        queryKey: ['fetchGetContext', impersonatedStateKey],
        queryFn: () => {
            return impersonatedStateKey
                ? fetchGetContextByStateKey({ stateKey: impersonatedStateKey })
                : fetchGetContext({});
        },
        enabled: getIsLoggedInCookie()
    });

    return {
        context: data?.data as GetContextResponse['data']
    };
}

export function useGetStateQuery<TStateResponse extends TGetStateResponse>() {
    const { context } = useGetContext();
    const stateKey = useMemo(() => context?.state_key as string, [context]);

    const { data, isLoading, isRefetching, isSuccess, isError } = useQuery({
        queryKey: ['fetchGetState', stateKey],
        queryFn: () => fetchGetStateByStateKey({ stateKey }),
        enabled: Boolean(stateKey) && getIsLoggedInCookie()
    });

    return {
        state: data?.data as TStateResponse['data'],
        prefillGetStartedState: normaliseState(data),
        isStateLoading: isLoading,
        isStateRefetching: isRefetching,
        isStateSuccess: isSuccess,
        isStateError: isError
    };
}

export function useGetApplicationQuery() {
    const { context } = useGetContext();
    const stateKey = useMemo(() => context?.state_key as string, [context]);

    const { data, refetch, isLoading, isRefetching, isSuccess, isError } =
        useQuery<GetApplicationResponse>({
            queryKey: ['fetchGetApplication', stateKey],
            queryFn: () => fetchGetApplication({ stateKey }),
            enabled: Boolean(stateKey) && getIsLoggedInCookie()
        });

    return {
        application: data?.data as GetApplicationResponse['data'],
        isApplicationLoading: isLoading,
        isApplicationRefetching: isRefetching,
        isApplicationSuccess: isSuccess,
        isApplicationError: isError,
        prefillGetStartedApplication: normaliseApplication(data),
        refetchApplication: refetch
    };
}

type TExperimentName = 'remove_vat_requirement_test' | 'signup_no_pg_test';

export function useGetSplitTestQuery(experimentName: TExperimentName) {
    const { context } = useGetContext();

    const stateKey = useMemo(() => context?.state_key as string, [context]);

    const { data: branch } = useQuery({
        queryKey: ['fetchSplitTest', stateKey, experimentName],
        queryFn: () => fetchGetSplitTest({ stateKey, experimentName }),
        select: (data) => data.data.branch.toLowerCase(),
        enabled: Boolean(stateKey)
    });

    return {
        branch
    };
}
